import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import {
  bridgePlatformApi,
  decodeActivationToken,
  tokenFromActivationUrl,
  type ActivationResult,
  type ActivatedDevice,
} from '../api/bridgePlatform';
import { getProperties } from '../api/properties';
import { getTenants } from '../api/tenants';

// Pre-shared admin token for the testing console. Will be replaced with
// Cognito JWT once the auth wiring lands; for now the token is plumbed
// through env so it can be rotated without rebuilds.
const ADMIN_TOKEN = process.env.REACT_APP_BRIDGE_ADMIN_TOKEN || '';

interface PersonMatch {
  id: string | number;
  name: string;
  property_id?: string | number;
}

/**
 * Build a lookup map from raw credential identifiers → person.
 * Same map is keyed by hex, card_number, and bluetooth_id so any of
 * those captured by a reader resolves to the same person record.
 */
function buildCredentialIndex(people: any[]): Map<string, PersonMatch> {
  const idx = new Map<string, PersonMatch>();
  for (const p of people) {
    const personId = p.uuid || p.id;
    const m: PersonMatch = {
      id: personId,
      name:
        p.name ||
        [p.first_name, p.last_name].filter(Boolean).join(' ').trim() ||
        p.email ||
        String(personId),
      property_id: p.property_id,
    };
    if (p.credential_raw_hex)
      idx.set(String(p.credential_raw_hex).toUpperCase(), m);
    if (p.credential_number) idx.set(`#${String(p.credential_number)}`, m);
    if (p.bluetooth_id) {
      const bt = String(p.bluetooth_id).toUpperCase();
      idx.set(`bt:${bt}`, m);
      if (/^[0-9A-F:]{17}$/.test(bt)) idx.set(bt, m);
    }
    // Intrlock-iOS BLE: phone advertises the user's tenantUUID = personId.
    // Index on the upper-case full ID *and* its 8-char prefix so reads
    // from the local-name path (Intrlock-XXXXXXXX) also match.
    if (personId) {
      const upper = String(personId).toUpperCase();
      idx.set(upper, m);
      const prefix = upper.replace(/-/g, '').slice(0, 8);
      if (prefix.length === 8) idx.set(prefix, m);
    }
  }
  return idx;
}

function matchPerson(
  evt: { rawHex?: string; cardNumber?: number; bluetooth_id?: string },
  idx: Map<string, PersonMatch>
): PersonMatch | undefined {
  if (evt.bluetooth_id) {
    const m =
      idx.get(`bt:${String(evt.bluetooth_id).toUpperCase()}`) ??
      idx.get(String(evt.bluetooth_id).toUpperCase());
    if (m) return m;
  }
  if (evt.rawHex) {
    const m = idx.get(String(evt.rawHex).toUpperCase());
    if (m) return m;
  }
  if (evt.cardNumber !== undefined) {
    const m = idx.get(`#${evt.cardNumber}`);
    if (m) return m;
  }
  return undefined;
}

type Mode = 'serial' | 'token';

const ROLE_OPTIONS = [
  'hub',
  'relay',
  'nvr',
  'reader',
  'intercom-panel',
  'camera',
  'doorbell',
  'access-control-panel',
  'siren',
] as const;

/**
 * Intrlock Bridge — Device Activation (testing page)
 * --------------------------------------------------
 * Standalone page for activating Bridge devices against the new
 * lambda-provisioning Lambda. Two paths:
 *   - Serial + PIN (typed from the device sticker)
 *   - QR token (paste the URL or token directly; mobile-app handles camera scan)
 *
 * This page is super-admin only and lives at /admin/intrlock-bridge.
 * It's a dev/test tool — once the platform graduates, the activation
 * flow will be folded into the device install workflow proper.
 */
export const IntrlockBridge: React.FC = () => {
  const [mode, setMode] = useState<Mode>('serial');

  // Serial+PIN form state
  const [serial, setSerial] = useState('');
  const [pin, setPin] = useState('');

  // Token form state
  const [tokenInput, setTokenInput] = useState('');

  // Common state
  const [customerId, setCustomerId] = useState('cust_test');
  const [siteId, setSiteId] = useState('site_test');
  const [role, setRole] = useState<string>('relay');
  const [roleConfigText, setRoleConfigText] = useState(
    JSON.stringify(
      { driver: 'simulated', channels: [{ channel: 1, label: 'Front' }] },
      null,
      2
    )
  );

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ActivationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Persistent list of all previously-activated devices.
  const [devices, setDevices] = useState<ActivatedDevice[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [devicesError, setDevicesError] = useState<string | null>(null);

  // Map credential identifiers → person for enriching credential_read events
  const [credIndex, setCredIndex] = useState<Map<string, PersonMatch>>(
    new Map()
  );

  useEffect(() => {
    void (async () => {
      try {
        const people: any = await getTenants({ per_page: 1000 });
        const arr: any[] = Array.isArray(people) ? people : people?.items ?? [];
        setCredIndex(buildCredentialIndex(arr));
      } catch {
        // non-fatal — events just won't show person names
      }
    })();
  }, []);

  const reloadDevices = useCallback(async () => {
    if (!ADMIN_TOKEN) {
      setDevicesError('REACT_APP_BRIDGE_ADMIN_TOKEN not configured at build time');
      setDevicesLoading(false);
      return;
    }
    setDevicesLoading(true);
    setDevicesError(null);
    try {
      const list = await bridgePlatformApi.listDevices(
        { status: 'activated' },
        ADMIN_TOKEN
      );
      setDevices(list);
    } catch (e) {
      setDevicesError((e as Error).message);
    } finally {
      setDevicesLoading(false);
    }
  }, []);

  useEffect(() => {
    void reloadDevices();
  }, [reloadDevices]);

  const decodedFromInput = (() => {
    if (mode !== 'token' || !tokenInput.trim()) return null;
    const raw = tokenInput.trim();
    const t = raw.startsWith('http') ? tokenFromActivationUrl(raw) : raw;
    if (!t) return null;
    return decodeActivationToken(t);
  })();

  async function submit() {
    setError(null);
    setResult(null);
    setSubmitting(true);
    try {
      let parsedRoleConfig: Record<string, unknown> = {};
      if (roleConfigText.trim()) {
        try {
          parsedRoleConfig = JSON.parse(roleConfigText) as Record<string, unknown>;
        } catch (e) {
          throw new Error(`Role config is not valid JSON: ${(e as Error).message}`);
        }
      }

      let res: ActivationResult;
      if (mode === 'serial') {
        if (!serial.trim() || !pin.trim()) {
          throw new Error('Serial and PIN are required');
        }
        res = await bridgePlatformApi.activateBySerial({
          serial: serial.trim().toUpperCase(),
          pin: pin.trim().toUpperCase(),
          customerId: customerId.trim(),
          siteId: siteId.trim(),
          role,
          roleConfig: parsedRoleConfig,
        });
        void reloadDevices();
      } else {
        const raw = tokenInput.trim();
        if (!raw) throw new Error('Paste an activation URL or token');
        const t = raw.startsWith('http') ? tokenFromActivationUrl(raw) : raw;
        if (!t) throw new Error('Could not extract activation token from input');
        res = await bridgePlatformApi.activateByToken({
          token: t,
          customerId: customerId.trim(),
          siteId: siteId.trim(),
          role,
          roleConfig: parsedRoleConfig,
        });
        void reloadDevices();
      }
      setResult(res);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="shadow-page-frame space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Intrlock Bridge</h1>
        <p className="text-sm text-gray-500 mt-1">
          Activate a Bridge device. Use the PIN printed on the sticker, or
          paste a scanned QR-code URL. This is a testing console — the full
          install workflow will live elsewhere once the platform is GA.
        </p>
      </div>

      <Card>
        <div className="space-y-4">
          {/* Mode toggle */}
          <div className="flex gap-2">
            <Button
              variant={mode === 'serial' ? 'primary' : 'secondary'}
              onClick={() => setMode('serial')}
            >
              Serial + PIN
            </Button>
            <Button
              variant={mode === 'token' ? 'primary' : 'secondary'}
              onClick={() => setMode('token')}
            >
              QR Token
            </Button>
          </div>

          {mode === 'serial' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Serial
                </label>
                <Input
                  placeholder="BR-2604-RELAY-XXXXXXXX"
                  value={serial}
                  onChange={(e) => setSerial(e.target.value)}
                  className="font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Activation PIN
                </label>
                <Input
                  placeholder="XXX-XXX"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  className="font-mono"
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Activation URL or Token
              </label>
              <Input
                placeholder="https://activate.intrlock.com/a/eyJ... or paste token directly"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                className="font-mono text-xs"
              />
              {decodedFromInput && (
                <div className="mt-2 p-3 bg-gray-50 rounded text-xs space-y-1 font-mono">
                  <div>
                    <span className="text-gray-500">serial:</span>{' '}
                    {decodedFromInput.serial}
                  </div>
                  <div>
                    <span className="text-gray-500">model:</span>{' '}
                    {decodedFromInput.model}
                  </div>
                  <div>
                    <span className="text-gray-500">fingerprint:</span>{' '}
                    {decodedFromInput.fingerprint}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Customer ID
              </label>
              <Input
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Site ID
              </label>
              <Input
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Role Config (JSON)
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-xs font-mono"
              rows={8}
              value={roleConfigText}
              onChange={(e) => setRoleConfigText(e.target.value)}
            />
          </div>

          <div className="flex gap-2 items-center">
            <Button
              variant="primary"
              onClick={submit}
              disabled={submitting}
            >
              {submitting ? 'Activating…' : 'Activate Device'}
            </Button>
            {error && (
              <span className="text-sm text-red-600">{error}</span>
            )}
          </div>
        </div>
      </Card>

      {result && (
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-green-600">
                ✓ Activation succeeded
              </span>{' '}
              <span className="font-mono text-xs">{result.serial}</span>{' '}
              <span className="text-xs text-gray-500">→ {result.role}</span>
            </div>
            <Badge status="active">activated</Badge>
          </div>
        </Card>
      )}

      <DeviceTable
        devices={devices}
        loading={devicesLoading}
        error={devicesError}
        onRefresh={() => void reloadDevices()}
        credIndex={credIndex}
      />
    </div>
  );
};

// ─── Device Table ────────────────────────────────────────────────────────

const DeviceTable: React.FC<{
  devices: ActivatedDevice[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  credIndex: Map<string, PersonMatch>;
}> = ({ devices, loading, error, onRefresh, credIndex }) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (s: string) => {
    const next = new Set(expanded);
    if (next.has(s)) next.delete(s);
    else next.add(s);
    setExpanded(next);
  };
  return (
    <Card>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Activated Devices</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              All Bridge devices that have been activated. Click a row to
              expand controls, telemetry, and recent events.
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={onRefresh} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </Button>
        </div>
        {error && <div className="text-sm text-red-600">{error}</div>}
        {!error && devices.length === 0 && !loading && (
          <div className="text-sm text-gray-500">
            No activated devices yet. Activate one above.
          </div>
        )}

        {devices.length > 0 && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left font-medium w-8"></th>
                  <th className="px-3 py-2 text-left font-medium">Serial</th>
                  <th className="px-3 py-2 text-left font-medium">Role</th>
                  <th className="px-3 py-2 text-left font-medium">Network</th>
                  <th className="px-3 py-2 text-left font-medium">Signal</th>
                  <th className="px-3 py-2 text-left font-medium">Month Usage</th>
                  <th className="px-3 py-2 text-left font-medium">As of</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {devices.map((d) => (
                  <DeviceRow
                    key={d.serial}
                    device={d}
                    expanded={expanded.has(d.serial)}
                    onToggle={() => toggle(d.serial)}
                    onChanged={onRefresh}
                    credIndex={credIndex}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
};

const DeviceRow: React.FC<{
  device: ActivatedDevice;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => void;
  credIndex: Map<string, PersonMatch>;
}> = ({ device, expanded, onToggle, onChanged, credIndex }) => {
  const monthBytes =
    (device.cellularUsage?.monthRxBytes ?? 0) +
    (device.cellularUsage?.monthTxBytes ?? 0);
  const sig = device.modem?.signalQuality;
  return (
    <>
      <tr
        onClick={onToggle}
        className="cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <td className="px-3 py-2">
          <span className="text-gray-400 font-mono text-xs">
            {expanded ? '▾' : '▸'}
          </span>
        </td>
        <td className="px-3 py-2">
          <div className="font-mono text-xs">{device.serial}</div>
          {device.displayName && (
            <div className="text-xs text-gray-500">{device.displayName}</div>
          )}
          {device.propertyId !== undefined && device.propertyId !== null && (
            <div className="text-xs text-emerald-600">
              property {device.propertyId}
            </div>
          )}
        </td>
        <td className="px-3 py-2">
          <Badge status="active">{device.role}</Badge>
          {device.roleConfig?.driver && (
            <span className="ml-1 text-xs text-gray-500">
              · {String(device.roleConfig.driver)}
            </span>
          )}
        </td>
        <td className="px-3 py-2">
          {device.hasCellular ? (
            <span className="inline-flex items-center gap-1.5">
              <Badge color="purple">cellular</Badge>
              {device.modem?.carrier && (
                <span className="text-xs text-gray-600">
                  {device.modem.carrier}
                </span>
              )}
            </span>
          ) : (
            <Badge color="gray">LAN/Wi-Fi</Badge>
          )}
        </td>
        <td className="px-3 py-2">
          {device.hasCellular ? (
            <SignalBars
              quality={sig}
              tech={device.modem?.accessTech}
            />
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
        </td>
        <td className="px-3 py-2 font-mono text-xs">
          {device.hasCellular ? formatBytes(monthBytes) : '—'}
        </td>
        <td className="px-3 py-2 text-xs text-gray-500">
          {device.cellularUsage?.asOf
            ? new Date(device.cellularUsage.asOf).toLocaleString()
            : device.activatedAt
            ? new Date(device.activatedAt).toLocaleString()
            : '—'}
        </td>
        <td className="px-3 py-2 text-right">
          <Badge status={device.status}>{device.status}</Badge>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50">
          <td colSpan={8} className="px-4 py-4">
            <DeviceDetails
              device={device}
              onChanged={onChanged}
              credIndex={credIndex}
            />
          </td>
        </tr>
      )}
    </>
  );
};

const SignalBars: React.FC<{ quality?: number; tech?: string }> = ({
  quality,
  tech,
}) => {
  if (quality === undefined) return <span className="text-xs text-gray-400">—</span>;
  const filled = Math.ceil((quality / 100) * 4);
  const color =
    quality >= 70
      ? 'bg-green-500'
      : quality >= 40
      ? 'bg-yellow-500'
      : 'bg-red-500';
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-flex items-end gap-0.5 h-3">
        {[1, 2, 3, 4].map((b) => (
          <span
            key={b}
            className={`w-1 ${b <= filled ? color : 'bg-gray-200'}`}
            style={{ height: `${b * 25}%` }}
          />
        ))}
      </span>
      <span className="text-xs text-gray-600 font-mono">{quality}%</span>
      {tech && (
        <span className="text-xs text-gray-400 uppercase">{tech}</span>
      )}
    </span>
  );
};

const DeviceDetails: React.FC<{
  device: ActivatedDevice;
  onChanged: () => void;
  credIndex: Map<string, PersonMatch>;
}> = ({ device, onChanged, credIndex }) => {
  const [refreshing, setRefreshing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Property assignment state
  const [properties, setProperties] = useState<Array<{ id: string | number; name: string }>>([]);
  const [propertyId, setPropertyId] = useState<string>(
    device.propertyId !== undefined && device.propertyId !== null
      ? String(device.propertyId)
      : ''
  );
  const [displayName, setDisplayName] = useState<string>(device.displayName ?? '');
  const [savingAssign, setSavingAssign] = useState(false);
  const [assignSaved, setAssignSaved] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const list: any = await getProperties();
        const arr: any[] = Array.isArray(list) ? list : (list?.items ?? []);
        setProperties(
          arr.map((p: any) => ({
            id: p.id,
            name: p.name ?? p.address ?? `Property ${p.id}`,
          }))
        );
      } catch {
        // Properties API failure is non-fatal; user can still use other tabs
      }
    })();
  }, []);

  async function saveAssignment() {
    if (!ADMIN_TOKEN) {
      setActionError('Admin token not configured');
      return;
    }
    setSavingAssign(true);
    setActionError(null);
    setAssignSaved(false);
    try {
      await bridgePlatformApi.assignDevice(device.serial, {
        propertyId: propertyId.trim() === '' ? null : propertyId,
        displayName: displayName.trim() === '' ? null : displayName.trim(),
        adminToken: ADMIN_TOKEN,
      });
      setAssignSaved(true);
      onChanged();
      setTimeout(() => setAssignSaved(false), 2000);
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setSavingAssign(false);
    }
  }

  const channels =
    device.roleConfig?.channels?.map((c) => ({
      channel: c.channel,
      gpioPin: c.gpioPin,
      label: c.label,
    })) ?? [];

  async function refresh() {
    if (!ADMIN_TOKEN) {
      setActionError('Admin token not configured');
      return;
    }
    setRefreshing(true);
    setActionError(null);
    try {
      await bridgePlatformApi.refreshUsage(device.serial, ADMIN_TOKEN);
      // Wait a beat for the device to post, then refresh the parent
      await new Promise((r) => setTimeout(r, 3500));
      onChanged();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setRefreshing(false);
    }
  }

  async function deleteDevice() {
    if (!ADMIN_TOKEN) {
      setActionError('Admin token not configured');
      return;
    }
    if (
      !window.confirm(
        `Delete ${device.serial}?\n\nThis revokes the IoT certificate, deletes the AWS IoT thing, and removes the device record. The agent will lose its activation. This cannot be undone.`
      )
    ) {
      return;
    }
    setDeleting(true);
    setActionError(null);
    try {
      await bridgePlatformApi.deleteDevice(device.serial, ADMIN_TOKEN);
      onChanged();
    } catch (e) {
      setActionError((e as Error).message);
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Top section: device info + cellular details + actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <InfoBlock title="Device">
          <Field label="Serial" value={device.serial} mono />
          <Field label="Model" value={device.model} />
          <Field label="Role" value={device.role} />
          <Field
            label="Driver"
            value={String(device.roleConfig?.driver ?? '—')}
          />
          <Field label="Customer" value={device.customerId ?? '—'} />
          <Field label="Site" value={device.siteId ?? '—'} />
          <Field
            label="Activated"
            value={
              device.activatedAt
                ? new Date(device.activatedAt).toLocaleString()
                : '—'
            }
          />
        </InfoBlock>

        <InfoBlock title={device.hasCellular ? 'Cellular' : 'Network'}>
          {device.hasCellular ? (
            <>
              <Field
                label="Interface"
                value={device.cellularUsage?.iface ?? 'wwan0'}
                mono
              />
              <Field label="Carrier" value={device.modem?.carrier ?? '—'} />
              <Field
                label="Operator ID"
                value={device.modem?.operatorId ?? '—'}
                mono
              />
              <Field
                label="Access Tech"
                value={device.modem?.accessTech?.toUpperCase() ?? '—'}
              />
              <Field
                label="Signal"
                value={
                  device.modem?.signalQuality !== undefined
                    ? `${device.modem.signalQuality}%`
                    : '—'
                }
              />
              <Field label="State" value={device.modem?.state ?? '—'} />
            </>
          ) : (
            <Field
              label="Connectivity"
              value="LAN / Wi-Fi (no cellular reported)"
            />
          )}
        </InfoBlock>

        <InfoBlock title="Usage (this month)">
          {device.hasCellular && device.cellularUsage ? (
            <>
              <Field
                label="Total"
                value={formatBytes(
                  (device.cellularUsage.monthRxBytes ?? 0) +
                    (device.cellularUsage.monthTxBytes ?? 0)
                )}
                mono
              />
              <Field
                label="Down (RX)"
                value={formatBytes(device.cellularUsage.monthRxBytes ?? 0)}
                mono
              />
              <Field
                label="Up (TX)"
                value={formatBytes(device.cellularUsage.monthTxBytes ?? 0)}
                mono
              />
              <Field
                label="Today (RX)"
                value={formatBytes(device.cellularUsage.todayRxBytes ?? 0)}
                mono
              />
              <Field
                label="Today (TX)"
                value={formatBytes(device.cellularUsage.todayTxBytes ?? 0)}
                mono
              />
              <Field
                label="As of"
                value={new Date(device.cellularUsage.asOf).toLocaleString()}
              />
              <div className="pt-1">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={refresh}
                  disabled={refreshing}
                >
                  {refreshing ? 'Refreshing…' : 'Refresh Cell Usage'}
                </Button>
              </div>
            </>
          ) : (
            <div className="text-xs text-gray-500">
              No cellular data yet. The agent reports usage every 5 minutes
              when wwan0 is detected.
            </div>
          )}
        </InfoBlock>
      </div>

      {/* Property assignment */}
      <div className="bg-white border border-gray-200 rounded-lg p-3">
        <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
          Assignment
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Property
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
            >
              <option value="">— Unassigned —</option>
              {properties.map((p) => (
                <option key={String(p.id)} value={String(p.id)}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Display name
            </label>
            <Input
              placeholder="e.g. Front Gate Bridge"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={saveAssignment}
              disabled={savingAssign}
            >
              {savingAssign ? 'Saving…' : 'Save Assignment'}
            </Button>
            {assignSaved && (
              <span className="text-xs text-green-600">✓ saved</span>
            )}
          </div>
        </div>
      </div>

      {/* Relay controls */}
      {device.role === 'relay' && (
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
            Controls
          </div>
          <RelayControlPanelInline
            serial={device.serial}
            channels={channels.length > 0 ? channels : undefined}
          />
        </div>
      )}

      {/* Reader info */}
      {device.role === 'reader' && (
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
            Reader Configuration
          </div>
          <ReaderInfoBlock device={device} credIndex={credIndex} />
        </div>
      )}

      {/* Access-control-panel: combined reader + relays + DC + REX */}
      {device.role === 'access-control-panel' && (
        <AccessControlBlock device={device} credIndex={credIndex} />
      )}

      {/* Recent events with per-event usage */}
      {device.recentEvents && device.recentEvents.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
            Recent Events ({device.recentEvents.length})
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-gray-100 text-gray-600">
                <tr>
                  <th className="px-3 py-1.5 text-left font-medium">When</th>
                  <th className="px-3 py-1.5 text-left font-medium">Event</th>
                  <th className="px-3 py-1.5 text-left font-medium">
                    {device.role === 'reader' ? 'Credential' : 'Channel'}
                  </th>
                  <th className="px-3 py-1.5 text-right font-medium">Bytes used</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {device.recentEvents.slice(0, 20).map((evt, i) => {
                  const match = (evt.type === 'credential_read' || evt.type === 'keypress')
                    ? matchPerson(evt, credIndex)
                    : undefined;
                  return (
                  <tr key={i}>
                    <td className="px-3 py-1.5 text-gray-500">
                      {new Date(evt.at).toLocaleTimeString()}
                    </td>
                    <td className="px-3 py-1.5">
                      {evt.type === 'credential_read' && (
                        <span className="text-emerald-700">
                          card read
                          {match && (
                            <span className="ml-1 font-medium text-green-700">
                              · {match.name}
                            </span>
                          )}
                        </span>
                      )}
                      {evt.type === 'keypress' && (
                        <span className="text-emerald-700">keypress</span>
                      )}
                      {evt.type === 'command_executed' && (
                        <span className="text-green-700">
                          {evt.cmd?.action ?? 'command'}
                          {evt.cmd?.durationMs !== undefined && (
                            <span className="text-gray-400">
                              {' '}({evt.cmd.durationMs}ms)
                            </span>
                          )}
                        </span>
                      )}
                      {evt.type === 'command_error' && (
                        <span className="text-red-600">{evt.error ?? 'error'}</span>
                      )}
                      {evt.type === 'event_usage' && (
                        <span className="text-gray-400">usage sample</span>
                      )}
                      {evt.type === 'door_open' && (
                        <span className="text-orange-600">🚪 door opened</span>
                      )}
                      {evt.type === 'door_closed' && (
                        <span className="text-green-700">🚪 door closed</span>
                      )}
                      {evt.type === 'rex_press' && (
                        <span className="text-purple-700">⏵ REX press</span>
                      )}
                      {evt.type === 'rex_release' && (
                        <span className="text-purple-400">REX release</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      {evt.cmd?.channel !== undefined
                        ? `ch${evt.cmd.channel}`
                        : evt.type === 'credential_read'
                        ? (
                          <span className="font-mono">
                            {evt.format ?? `${evt.bitCount}-bit`}
                            {evt.facilityCode !== undefined && (
                              <span className="text-gray-500">
                                {' '}FC {evt.facilityCode}
                              </span>
                            )}
                            {evt.cardNumber !== undefined && (
                              <span className="text-gray-700">
                                {' '}#{evt.cardNumber}
                              </span>
                            )}
                            {evt.parityValid === false && (
                              <span className="text-red-600 ml-1">parity!</span>
                            )}
                          </span>
                        )
                        : evt.type === 'keypress'
                        ? <span className="font-mono">key {evt.key}</span>
                        : '—'}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono">
                      {evt.usage
                        ? formatBytes(evt.usage.totalBytes)
                        : '—'}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bottom: danger zone */}
      <div className="border-t border-gray-200 pt-3 flex items-center justify-between">
        <div className="text-xs text-gray-500">
          Thing: <span className="font-mono">{device.thingName ?? '—'}</span>
        </div>
        <div className="flex items-center gap-2">
          {actionError && (
            <span className="text-xs text-red-600">{actionError}</span>
          )}
          <Button
            variant="danger"
            size="sm"
            onClick={deleteDevice}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete Device'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const InfoBlock: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-3">
    <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
      {title}
    </div>
    <div className="space-y-1 text-xs">{children}</div>
  </div>
);

const Field: React.FC<{ label: string; value: string; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <div className="flex justify-between gap-3">
    <span className="text-gray-500">{label}:</span>
    <span className={mono ? 'font-mono' : ''}>{value}</span>
  </div>
);

const AccessControlBlock: React.FC<{
  device: ActivatedDevice;
  credIndex: Map<string, PersonMatch>;
}> = ({ device, credIndex }) => {
  const cfg = device.roleConfig as any;
  const reader = cfg?.reader ?? {};
  const relayChannels: Array<{ channel: number; gpioPin?: number; label?: string }> =
    cfg?.relays?.channels ?? [
      { channel: 1, gpioPin: 4, label: 'Door strike' },
      { channel: 2, gpioPin: 22, label: 'Aux 1' },
      { channel: 3, gpioPin: 6, label: 'Aux 2' },
      { channel: 4, gpioPin: 26, label: 'Aux 3' },
    ];
  const dcPin = cfg?.doorContact?.pin ?? 23;
  const rexPin = cfg?.rex?.pin ?? 24;
  const rexAutoPulse = cfg?.rex?.autoPulseChannel;

  // Latest derived states from recentEvents
  const events = device.recentEvents ?? [];
  const lastDoor = events.find((e) => e.type === 'door_open' || e.type === 'door_closed');
  const doorState =
    lastDoor?.type === 'door_open' ? 'open' : lastDoor?.type === 'door_closed' ? 'closed' : 'unknown';
  const lastRead = events.find((e) => e.type === 'credential_read' || e.type === 'keypress');
  const lastReadMatch = lastRead ? matchPerson(lastRead, credIndex) : undefined;
  const lastRex = events.find((e) => e.type === 'rex_press');
  const reads = events.filter((e) => e.type === 'credential_read').length;
  const keypresses = events.filter((e) => e.type === 'keypress').length;
  const rexPresses = events.filter((e) => e.type === 'rex_press').length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
            Reader
          </div>
          <div className="space-y-1 text-xs">
            <Field label="Name" value={String(reader.name ?? 'Reader')} />
            <Field label="D0" value={`GPIO ${reader.d0Pin ?? 17}`} mono />
            <Field label="D1" value={`GPIO ${reader.d1Pin ?? 27}`} mono />
            <Field label="Reads" value={String(reads)} mono />
            <Field label="Keypresses" value={String(keypresses)} mono />
            {lastRead && (
              <>
                <div className="border-t border-gray-100 mt-2 pt-2">
                  <div className="text-gray-500 mb-1">Last read</div>
                  <div className="font-mono text-[10px] break-all">
                    {lastRead.rawHex}
                  </div>
                  <div>
                    {lastReadMatch ? (
                      <span className="text-green-700">
                        ✓ {lastReadMatch.name}
                      </span>
                    ) : (
                      <span className="text-gray-400">(unmatched)</span>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
            Door Contact
          </div>
          <div className="space-y-1 text-xs">
            <Field label="Pin" value={`GPIO ${dcPin}`} mono />
            <div className="flex justify-between">
              <span className="text-gray-500">State:</span>
              {doorState === 'open' ? (
                <span className="text-orange-600 font-medium">🚪 OPEN</span>
              ) : doorState === 'closed' ? (
                <span className="text-green-700 font-medium">🚪 CLOSED</span>
              ) : (
                <span className="text-gray-400">unknown</span>
              )}
            </div>
            {lastDoor && (
              <Field
                label="Since"
                value={new Date(lastDoor.at).toLocaleTimeString()}
              />
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
            Request to Exit
          </div>
          <div className="space-y-1 text-xs">
            <Field label="Pin" value={`GPIO ${rexPin}`} mono />
            <Field label="Presses" value={String(rexPresses)} mono />
            {rexAutoPulse !== undefined && (
              <Field
                label="Auto-pulse"
                value={`Ch ${rexAutoPulse} (${(cfg?.rex?.autoPulseMs ?? 5000) / 1000}s)`}
              />
            )}
            {lastRex && (
              <Field
                label="Last press"
                value={new Date(lastRex.at).toLocaleTimeString()}
              />
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
            Wiring
          </div>
          <div className="space-y-1 text-xs text-gray-600">
            <div>D0/D1: {reader.d0Pin ?? 17}/{reader.d1Pin ?? 27}</div>
            <div>Relays: {relayChannels.map((c) => c.gpioPin).join(', ')}</div>
            <div>Door contact: {dcPin}</div>
            <div>REX: {rexPin}</div>
          </div>
        </div>
      </div>

      {/* Recent BLE sightings (when ble enabled) */}
      {(cfg?.ble && cfg.ble.enabled !== false) && (
        <BleSightings device={device} credIndex={credIndex} />
      )}

      <div>
        <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
          Relay Controls
        </div>
        <RelayControlPanelInline
          serial={device.serial}
          channels={relayChannels}
        />
      </div>
    </div>
  );
};

const BleSightings: React.FC<{
  device: ActivatedDevice;
  credIndex: Map<string, PersonMatch>;
}> = ({ device, credIndex }) => {
  const events = device.recentEvents ?? [];
  // De-dupe by MAC, keep most recent record (events come newest-first)
  const byMac = new Map<string, { mac: string; rssi?: number; name?: string; matched?: PersonMatch; lastAt: string; lastType: string }>();
  for (const e of events) {
    if (e.type !== 'ble_seen' && e.type !== 'credential_read') continue;
    const mac = e.bluetooth_id;
    if (!mac) continue;
    if (byMac.has(mac)) continue;
    byMac.set(mac, {
      mac,
      rssi: e.rssi,
      name: e.bleName,
      matched: matchPerson(e, credIndex),
      lastAt: e.at,
      lastType: e.type,
    });
  }
  const rows = Array.from(byMac.values()).slice(0, 12);
  if (rows.length === 0) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
        Recent BLE Sightings ({rows.length})
      </div>
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-100 text-gray-600">
            <tr>
              <th className="px-3 py-1.5 text-left font-medium">MAC</th>
              <th className="px-3 py-1.5 text-left font-medium">Name</th>
              <th className="px-3 py-1.5 text-left font-medium">RSSI</th>
              <th className="px-3 py-1.5 text-left font-medium">Person</th>
              <th className="px-3 py-1.5 text-right font-medium">Last seen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => (
              <tr key={r.mac}>
                <td className="px-3 py-1.5 font-mono">{r.mac}</td>
                <td className="px-3 py-1.5 text-gray-700">{r.name ?? '—'}</td>
                <td className="px-3 py-1.5 font-mono">
                  {r.rssi !== undefined ? `${r.rssi} dBm` : '—'}
                </td>
                <td className="px-3 py-1.5">
                  {r.matched ? (
                    <span className="text-green-700">✓ {r.matched.name}</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard?.writeText(r.mac);
                      }}
                      className="text-emerald-600 hover:underline"
                      title="Click to copy MAC; paste into a Person's Bluetooth ID field"
                    >
                      copy MAC
                    </button>
                  )}
                </td>
                <td className="px-3 py-1.5 text-right text-gray-500">
                  {new Date(r.lastAt).toLocaleTimeString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ReaderInfoBlock: React.FC<{
  device: ActivatedDevice;
  credIndex: Map<string, PersonMatch>;
}> = ({ device, credIndex }) => {
  const cfg = device.roleConfig as any;
  const reads = (device.recentEvents ?? []).filter(
    (e) => e.type === 'credential_read' || e.type === 'keypress'
  );
  const lastRead = reads[0];
  const lastMatch = lastRead ? matchPerson(lastRead, credIndex) : undefined;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div className="bg-white border border-gray-200 rounded-lg p-3">
        <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
          Wiegand Pins
        </div>
        <div className="space-y-1 text-xs">
          <Field label="D0 (green)" value={`GPIO ${cfg?.d0Pin ?? 17}`} mono />
          <Field label="D1 (white)" value={`GPIO ${cfg?.d1Pin ?? 27}`} mono />
          <Field
            label="End-of-card"
            value={`${cfg?.timeoutMs ?? 50}ms`}
            mono
          />
          <Field label="Reader name" value={String(cfg?.name ?? 'Reader')} />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-3">
        <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
          Last Read
        </div>
        {lastRead ? (
          <div className="space-y-1 text-xs">
            {lastMatch ? (
              <Field label="Person" value={`✓ ${lastMatch.name}`} />
            ) : (
              <Field label="Person" value="(unmatched)" />
            )}
            <Field
              label="Format"
              value={lastRead.format ?? `${lastRead.bitCount ?? '?'}-bit`}
            />
            {lastRead.facilityCode !== undefined && (
              <Field label="FC" value={String(lastRead.facilityCode)} mono />
            )}
            {lastRead.cardNumber !== undefined && (
              <Field
                label={lastRead.type === 'keypress' ? 'Key' : 'Card #'}
                value={String(lastRead.key ?? lastRead.cardNumber)}
                mono
              />
            )}
            {lastRead.rawHex && (
              <Field label="Raw" value={lastRead.rawHex} mono />
            )}
            <Field
              label="When"
              value={new Date(lastRead.at).toLocaleString()}
            />
            {lastRead.parityValid === false && (
              <Field label="Parity" value="FAILED" />
            )}
          </div>
        ) : (
          <div className="text-xs text-gray-500">
            No reads yet. Present a card to the reader.
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-3">
        <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
          Activity
        </div>
        <div className="space-y-1 text-xs">
          <Field label="Total reads" value={String(reads.length)} mono />
          <Field
            label="Card reads"
            value={String(reads.filter((r) => r.type === 'credential_read').length)}
            mono
          />
          <Field
            label="Keypresses"
            value={String(reads.filter((r) => r.type === 'keypress').length)}
            mono
          />
        </div>
      </div>
    </div>
  );
};

function formatBytes(bytes: number): string {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

interface RelayControlPanelInlineProps {
  serial: string;
  /** Channels from device.roleConfig.channels — falls back to 1-4 with default GPIO map */
  channels?: Array<{ channel: number; gpioPin?: number; label?: string }>;
}

const FALLBACK_CHANNELS = [
  { channel: 1, gpioPin: 4, label: 'J2' },
  { channel: 2, gpioPin: 22, label: 'J3' },
  { channel: 3, gpioPin: 6, label: 'J4' },
  { channel: 4, gpioPin: 26, label: 'J5' },
];

const RelayControlPanelInline: React.FC<RelayControlPanelInlineProps> = ({
  serial,
  channels: providedChannels,
}) => {
  const channels = providedChannels && providedChannels.length > 0
    ? providedChannels
    : FALLBACK_CHANNELS;
  const [pulseSec, setPulseSec] = useState<Record<number, string>>(() =>
    Object.fromEntries(channels.map((c) => [c.channel, '5']))
  );
  const [busyChannel, setBusyChannel] = useState<number | null>(null);
  const [lastResult, setLastResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function send(
    channel: number,
    action: 'pulse' | 'on' | 'off' | 'toggle'
  ) {
    setError(null);
    setLastResult(null);
    if (!ADMIN_TOKEN) {
      setError('REACT_APP_BRIDGE_ADMIN_TOKEN not configured at build time');
      return;
    }
    setBusyChannel(channel);
    try {
      const durationMs =
        action === 'pulse'
          ? Math.max(50, Math.round(parseFloat(pulseSec[channel] || '5') * 1000))
          : undefined;
      await bridgePlatformApi.sendRelayCommand(serial, {
        channel,
        action,
        durationMs,
        adminToken: ADMIN_TOKEN,
      });
      setLastResult(
        `ch${channel} ${action}${durationMs ? ` (${durationMs}ms)` : ''} → ok`
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyChannel(null);
    }
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {channels.map((c) => (
          <div
            key={c.channel}
            className="border border-gray-200 rounded-lg p-3 space-y-2"
          >
            <div className="flex items-baseline justify-between">
              <div className="text-sm font-medium">
                {c.label ? `${c.label}` : `Channel ${c.channel}`}
              </div>
              <div className="text-xs text-gray-400">
                {c.gpioPin !== undefined ? `GPIO ${c.gpioPin}` : `ch${c.channel}`}
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <Input
                type="number"
                step="0.1"
                min="0.05"
                value={pulseSec[c.channel] ?? '5'}
                onChange={(e) =>
                  setPulseSec({ ...pulseSec, [c.channel]: e.target.value })
                }
                className="w-16 text-xs"
              />
              <span className="text-xs text-gray-500">sec</span>
              <Button
                variant="primary"
                size="sm"
                onClick={() => send(c.channel, 'pulse')}
                disabled={busyChannel === c.channel}
                className="ml-auto"
              >
                Pulse
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-1.5">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => send(c.channel, 'on')}
                disabled={busyChannel === c.channel}
              >
                On
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => send(c.channel, 'off')}
                disabled={busyChannel === c.channel}
              >
                Off
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => send(c.channel, 'toggle')}
                disabled={busyChannel === c.channel}
              >
                Toggle
              </Button>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
};
